import React from 'react';
import PropTypes from 'prop-types';
import {
  REWARD_CARD_TYPES,
  REWARD_TYPES,
  isInventoryManagedRewardType
} from '@/utils/constants/rewardsConstants';
import InventorySection from './InventorySection';
import ToggleRow from './ToggleRow';
import InventorySectionForCardTypeBoth from './InventorySectionForCardTypeBoth';
import {Constants} from '@/utils/constants/commonConstants';
import {useAdditionalAttributesLogic} from './useAdditionalAttributesSection-hook';
import {Counter} from '@/components/atoms';
import LabelWithTooltip from '@/components/atoms/LabelWithTooltip/LabelWithTooltip';
import {ALERT_MIN, ALERT_MAX} from '@/utils/constants/rewardsDisplayConstants';

/**
 * AdditionalAttributesSection component for reward display and stock settings
 * @param {Object} props - Component props
 * @param {Object} props.formData - Form data containing displayOutOfStock, inStock, and inventory fields
 * @param {Function} props.handleFormChange - Function to handle form field changes
 * @param {Function} props.handleValidation - Function to handle validation state
 * @returns {JSX.Element} The rendered AdditionalAttributesSection component
 */
const AdditionalAttributesSection = ({
  formData,
  handleFormChange,
  handleValidation,
  validation
}) => {
  const {
    handleQuotaChange,
    handleLeftOverInventoryChange,
    handleLimitPerStoreChange,
    handleMaxQtyPerOrderChange,
    handleQuotaDigitalCardChange,
    handleLeftOverInventoryDigitalCardChange,
    handleLimitPerStoreDigitalCardChange,
    handleQuotaPhysicalCardChange,
    handleLeftOverInventoryPhysicalCardChange,
    handleLimitPerStorePhysicalCardChange,
    handleDisplayOutOfStockChange,
    handleUnlimitedInventoryChange,
    handleInStockChange
  } = useAdditionalAttributesLogic({
    formData,
    handleFormChange,
    handleValidation,
    validation
  });

  // Check if reward type supports inventory
  const inventoryRewardType = isInventoryManagedRewardType(
    formData?.rewardType,
    formData?.cardType
  );

  const isInventoryVisaCardTypeBoth =
    formData?.rewardType === REWARD_TYPES.VISA_CARD &&
    formData?.cardType === REWARD_CARD_TYPES.Both;

  // Check if reward type is BONUS_PRODUCTS (Alert Display Count is shown at reward level, not per-product)
  const isBonusProducts = formData?.rewardType === REWARD_TYPES.BONUS_PRODUCTS;

  // Handler for Alert Display Count change
  const handleAlertDisplayCountChange = (value) => {
    if (value === '' || value === null || value === undefined) {
      handleFormChange('alertDisplayCount', '');
      return;
    }
    const numValue = Number(value);
    const clampedValue = Math.max(ALERT_MIN, Math.min(ALERT_MAX, numValue));
    handleFormChange('alertDisplayCount', clampedValue);
  };

  // Handler for Show on Home Page change (moved from Display tab)
  const handleShowOnHomePageChange = (value) => {
    handleFormChange('showOnHomePage', Boolean(value));
  };

  return (
    <div className='flex flex-col gap-6'>
      <h3 className='text-base font-bold text-white'>Additional Attributes</h3>

      {/* Show InventoryFieldsRow for inventory reward types */}
      {inventoryRewardType && (
        <InventorySection
          formData={formData}
          handleFormChange={handleFormChange}
          handleValidation={handleValidation}
          handleQuotaChange={handleQuotaChange}
          handleLeftOverInventoryChange={handleLeftOverInventoryChange}
          handleLimitPerStoreChange={handleLimitPerStoreChange}
          handleMaxQtyPerOrderChange={handleMaxQtyPerOrderChange}
          isQuotaDisabled={formData?.leftOverInventory == Constants.Unlimited}
          quotaValidationError={validation?.quotaValid}
          limitValidationError={validation?.limitPerStoreValid}
          maxQtyPerOrderValidationError={validation?.maxQtyPerOrderValid}
        />
      )}

      {/* Show InventoryFieldsRowForCardTypeBoth for Visa Card - Both */}
      {isInventoryVisaCardTypeBoth && (
        <InventorySectionForCardTypeBoth
          formData={formData}
          handleFormChange={handleFormChange}
          handleValidation={handleValidation}
          handleQuotaDigitalCardChange={handleQuotaDigitalCardChange}
          handleLeftOverInventoryDigitalCardChange={
            handleLeftOverInventoryDigitalCardChange
          }
          handleLimitPerStoreDigitalCardChange={
            handleLimitPerStoreDigitalCardChange
          }
          handleQuotaPhysicalCardChange={handleQuotaPhysicalCardChange}
          handleLeftOverInventoryPhysicalCardChange={
            handleLeftOverInventoryPhysicalCardChange
          }
          handleLimitPerStorePhysicalCardChange={
            handleLimitPerStorePhysicalCardChange
          }
          isQuotaDigitalCardDisabled={
            formData?.leftOverInventoryDigitalCard == Constants.Unlimited
          }
          isQuotaPhysicalCardDisabled={
            formData?.leftOverInventoryPhysicalCard == Constants.Unlimited
          }
          quotaDigitalCardValidationError={validation?.quotaDigitalCardValid}
          quotaPhysicalCardValidationError={validation?.quotaPhysicalCardValid}
          limitPerStoreDigitalCardValidationError={
            validation?.limitPerStoreDigitalCardValid
          }
          limitPerStorePhysicalCardValidationError={
            validation?.limitPerStorePhysicalCardValid
          }
        />
      )}

      {/* Alert Display Count - Show only for BONUS_PRODUCTS reward type */}
      {/* Must be > 0 to proceed to next step */}
      {isBonusProducts && (
        <div className='flex flex-col gap-2'>
          <LabelWithTooltip
            className='text-[12px] text-[#d0d0d0]'
            label={'Alert Display Count*'}
          />
          <Counter
            feedbackType='info'
            value={
              formData?.alertDisplayCount === '' ||
              formData?.alertDisplayCount === null ||
              formData?.alertDisplayCount === undefined
                ? 0
                : formData?.alertDisplayCount
            }
            onUpdate={handleAlertDisplayCountChange}
            minValue={ALERT_MIN}
            maxValue={ALERT_MAX}
            size='medium'
            className='w-20'
            style={{width: '76px'}}
            disabled={false}
          />
        </div>
      )}

      {/* ToggleRow - Always shown, conditionally shows unlimited inventory toggle based on reward type */}
      {/* Show on Home Page toggle is now in ToggleRow as the last toggle */}
      <ToggleRow
        formData={formData}
        handleDisplayOutOfStockChange={handleDisplayOutOfStockChange}
        handleUnlimitedInventoryChange={handleUnlimitedInventoryChange}
        handleInStockChange={handleInStockChange}
        handleShowOnHomePageChange={handleShowOnHomePageChange}
      />
    </div>
  );
};

export default React.memo(AdditionalAttributesSection);

AdditionalAttributesSection.propTypes = {
  formData: PropTypes.object.isRequired,
  handleFormChange: PropTypes.func.isRequired,
  handleValidation: PropTypes.func.isRequired,
  validation: PropTypes.object
};
